import React, { useState, useEffect, DragEvent, useRef, useMemo } from 'react';
import Layout from '../../../components/Layout';
import { apiFetch, apiDownload } from '../../../lib/api';
import BACKEND_URL from '../../../config.js';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { formatCurrency } from '../../../utils/formatters';
import ApiInspector from '../../../components/ApiInspector';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import { API_DOCS } from './LipePage.api';

// Icona graffetta (allegati) — usata nelle azioni riga
const PaperclipIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

type Periodicita = 'MENSILE' | 'TRIMESTRALE';
type PeriodoTrim = 'Q1' | 'Q2' | 'Q3' | 'Q4';
type PeriodoMens = 'M01' | 'M02' | 'M03' | 'M04' | 'M05' | 'M06' | 'M07' | 'M08' | 'M09' | 'M10' | 'M11' | 'M12';
type Periodo = PeriodoTrim | PeriodoMens;

interface UtenteMin {
  id: number;
  nome: string;
}

interface AllegatoLipe {
  id: number;
  nomeFile: string;
  mimeType: string | null;
  uploadedAt: string;
  uploadedBy: UtenteMin;
}

interface Lipe {
  id: number;
  societaId: number;
  societa: { id: number; nome: string };
  anno: number;
  periodicita: Periodicita;
  periodo: Periodo;
  ivaDebito: number | null;
  ivaCredito: number | null;
  importoVersato: number | null;
  creditoRiportato: number | null;
  dataPresentazione: string | null;
  protocolloTelem: string | null;
  commento: string | null;
  uploadedAt: string;
  uploadedBy: UtenteMin;
  allegati: AllegatoLipe[];
}

const PERIODI_TRIM: PeriodoTrim[] = ['Q1', 'Q2', 'Q3', 'Q4'];
const PERIODI_MENS: PeriodoMens[] = ['M01', 'M02', 'M03', 'M04', 'M05', 'M06', 'M07', 'M08', 'M09', 'M10', 'M11', 'M12'];

const MESI_LABEL: Record<PeriodoMens, string> = {
  M01: 'Gennaio',  M02: 'Febbraio', M03: 'Marzo',     M04: 'Aprile',
  M05: 'Maggio',   M06: 'Giugno',   M07: 'Luglio',    M08: 'Agosto',
  M09: 'Settembre', M10: 'Ottobre',  M11: 'Novembre', M12: 'Dicembre',
};

const TRIM_LABEL: Record<PeriodoTrim, string> = {
  Q1: 'I trimestre (gen-mar)',
  Q2: 'II trimestre (apr-giu)',
  Q3: 'III trimestre (lug-set)',
  Q4: 'IV trimestre (ott-dic)',
};

function labelPeriodo(p: Periodo): string {
  if (p in TRIM_LABEL) return TRIM_LABEL[p as PeriodoTrim];
  return MESI_LABEL[p as PeriodoMens] || p;
}

export default function LipePage() {
  const [lipe, setLipe] = useState<Lipe[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [userRole, setUserRole] = useState<string>('');

  const {
    societaId: filterSocietaId,
    setSocietaId: setFilterSocietaId,
    listaSocieta: societaList,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  // Form Upload
  const [uploadSocietaId, setUploadSocietaId] = useState<string>('');
  const currentYear = new Date().getFullYear();
  const [uploadAnno, setUploadAnno] = useState<string>(currentYear.toString());
  const [uploadPeriodicita, setUploadPeriodicita] = useState<Periodicita>('TRIMESTRALE');
  const [uploadPeriodo, setUploadPeriodo] = useState<Periodo>('Q1');
  const [uploadIvaDebito, setUploadIvaDebito] = useState<string>('');
  const [uploadIvaCredito, setUploadIvaCredito] = useState<string>('');
  const [uploadVersato, setUploadVersato] = useState<string>('');
  const [uploadCreditoRiportato, setUploadCreditoRiportato] = useState<string>('');
  const [uploadDataPres, setUploadDataPres] = useState<string>('');
  const [uploadProtocollo, setUploadProtocollo] = useState<string>('');
  const [uploadCommento, setUploadCommento] = useState<string>('');
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Modale gestione allegati per LIPE esistente
  const [allegatiModal, setAllegatiModal] = useState<Lipe | null>(null);
  const [allegatiUploading, setAllegatiUploading] = useState(false);
  const modalFileInputRef = useRef<HTMLInputElement>(null);

  // Filtri lista
  const [filterAnno, setFilterAnno] = useState<string>('');
  const [filterPeriodicita, setFilterPeriodicita] = useState<string>('');

  // @api GET /auth/me -> {"utente":{"ruolo":"string"}}
  useEffect(() => {
    apiFetch<{ utente: { ruolo: string } }>('/auth/me')
      .then(d => setUserRole(d.utente.ruolo))
      .catch(() => {});
  }, []);

  // Auto-fill società upload
  useEffect(() => {
    if (isUnica && societaList.length === 1) {
      setUploadSocietaId(societaList[0].id.toString());
    } else if (filterSocietaId) {
      setUploadSocietaId(filterSocietaId);
    }
  }, [isUnica, societaList, filterSocietaId]);

  // Quando cambio periodicità, allineo il periodo al primo valore valido
  useEffect(() => {
    if (uploadPeriodicita === 'TRIMESTRALE') {
      if (!PERIODI_TRIM.includes(uploadPeriodo as PeriodoTrim)) setUploadPeriodo('Q1');
    } else {
      if (!PERIODI_MENS.includes(uploadPeriodo as PeriodoMens)) setUploadPeriodo('M01');
    }
  }, [uploadPeriodicita, uploadPeriodo]);

  const periodiDisponibili: Periodo[] = useMemo(
    () => (uploadPeriodicita === 'TRIMESTRALE' ? PERIODI_TRIM : PERIODI_MENS),
    [uploadPeriodicita]
  );

  // Anni 2015..2035
  const selectYears = useMemo(() => {
    const arr: number[] = [];
    for (let y = 2035; y >= 2015; y--) arr.push(y);
    return arr;
  }, []);

  useEffect(() => {
    fetchLipe();
  }, [pronta, filterSocietaId, filterAnno, filterPeriodicita]); // eslint-disable-line

  // @api GET /api/lipe?societaId=&anno=&periodicita= -> [{...lipe, allegati:[]}]
  const fetchLipe = async () => {
    if (!pronta) return;
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (filterSocietaId) params.append('societaId', filterSocietaId);
      if (filterAnno) params.append('anno', filterAnno);
      if (filterPeriodicita) params.append('periodicita', filterPeriodicita);

      const res = await apiFetch<Lipe[]>(`/api/lipe?${params.toString()}`);
      setLipe(res);

      // Se la modale allegati è aperta, refresh i dati di quella riga
      if (allegatiModal) {
        const refreshed = res.find(l => l.id === allegatiModal.id);
        if (refreshed) setAllegatiModal(refreshed);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const dropped = Array.from(e.dataTransfer.files);
      setUploadFiles(prev => [...prev, ...dropped]);
    }
  };

  const removeFileFromUpload = (idx: number) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== idx));
  };

  // @api POST /api/lipe FormData{societaId,anno,periodicita,periodo,ivaDebito?,ivaCredito?,importoVersato?,creditoRiportato?,dataPresentazione?,protocolloTelem?,commento?,files[]} -> {"success":true,"id":"number"}
  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isUnica && !uploadSocietaId) {
      alert('Seleziona la Società');
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('societaId', uploadSocietaId);
      formData.append('anno', uploadAnno);
      formData.append('periodicita', uploadPeriodicita);
      formData.append('periodo', uploadPeriodo);
      if (uploadIvaDebito !== '') formData.append('ivaDebito', uploadIvaDebito);
      if (uploadIvaCredito !== '') formData.append('ivaCredito', uploadIvaCredito);
      if (uploadVersato !== '') formData.append('importoVersato', uploadVersato);
      if (uploadCreditoRiportato !== '') formData.append('creditoRiportato', uploadCreditoRiportato);
      if (uploadDataPres) formData.append('dataPresentazione', uploadDataPres);
      if (uploadProtocollo) formData.append('protocolloTelem', uploadProtocollo);
      if (uploadCommento) formData.append('commento', uploadCommento);
      uploadFiles.forEach(f => formData.append('files', f));

      const res = await fetch(`${BACKEND_URL}/api/lipe`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.error || 'Errore di caricamento');
      }

      // Reset form
      setUploadFiles([]);
      setUploadIvaDebito('');
      setUploadIvaCredito('');
      setUploadVersato('');
      setUploadCreditoRiportato('');
      setUploadDataPres('');
      setUploadProtocollo('');
      setUploadCommento('');
      if (fileInputRef.current) fileInputRef.current.value = '';

      fetchLipe();
      alert('LIPE caricata con successo!');
    } catch (err: any) {
      console.error(err);
      alert(err.message || "Si è verificato un errore durante l'upload");
    } finally {
      setIsUploading(false);
    }
  };

  // @api GET /api/lipe/:id/allegati/:allegatoId/download -> file binary
  const handleDownloadAllegato = async (lipeId: number, allegatoId: number, nomeFile: string) => {
    try {
      await apiDownload(`/api/lipe/${lipeId}/allegati/${allegatoId}/download`, nomeFile);
    } catch (err) {
      console.error(err);
      alert('Errore durante il download del file');
    }
  };

  // @api POST /api/lipe/:id/allegati FormData{files[]} -> {"success":true,"ids":[...]}
  const handleAddAllegati = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!allegatiModal) return;
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      setAllegatiUploading(true);
      const formData = new FormData();
      Array.from(files).forEach(f => formData.append('files', f));

      const res = await fetch(`${BACKEND_URL}/api/lipe/${allegatiModal.id}/allegati`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.error || 'Errore upload allegati');
      }

      if (modalFileInputRef.current) modalFileInputRef.current.value = '';
      await fetchLipe();
    } catch (err: any) {
      console.error(err);
      alert(err.message || 'Errore durante upload allegati');
    } finally {
      setAllegatiUploading(false);
    }
  };

  // @api DELETE /api/lipe/:id/allegati/:allegatoId -> {"success":true}
  const handleDeleteAllegato = async (lipeId: number, allegatoId: number, nomeFile: string) => {
    if (!window.confirm(`Eliminare definitivamente l'allegato "${nomeFile}"?`)) return;
    try {
      await apiFetch(`/api/lipe/${lipeId}/allegati/${allegatoId}`, { method: 'DELETE' });
      await fetchLipe();
    } catch (err: any) {
      console.error(err);
      alert(err.message || "Errore durante l'eliminazione dell'allegato");
    }
  };

  // @api DELETE /api/lipe/:id -> {"success":true}
  const handleDelete = async (id: number) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa LIPE definitivamente, inclusi tutti gli allegati? L'azione non è reversibile.")) return;

    try {
      await apiFetch(`/api/lipe/${id}`, { method: 'DELETE' });
      fetchLipe();
    } catch (err: any) {
      console.error(err);
      alert(err.message || "Errore durante l'eliminazione");
    }
  };

  return (
    <Layout title="LIPE - Liquidazioni Periodiche IVA">
      {/* ── CARD UPLOAD ── */}
      <div className="bop-bilanci__upload-card">
        <h2 className="bop-bilanci__upload-title">Carica una Nuova LIPE</h2>

        <form onSubmit={handleUpload} className="bop-bilanci__upload-form">
          <div className="bop-bilanci__form-col">
            {!isUnica && (
              <div className="bop-form-group">
                <label className="bop-label">Società *</label>
                <select
                  value={uploadSocietaId}
                  onChange={e => setUploadSocietaId(e.target.value)}
                  className="bop-select"
                  required
                >
                  <option value="">Seleziona società...</option>
                  {societaList.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                </select>
              </div>
            )}

            <div className="bop-bilanci__form-row">
              <div className="bop-bilanci__form-row-item">
                <label className="bop-label">Anno *</label>
                <select
                  value={uploadAnno}
                  onChange={e => setUploadAnno(e.target.value)}
                  className="bop-select"
                  required
                >
                  {selectYears.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>

              <div className="bop-bilanci__form-row-item">
                <label className="bop-label">Periodicità *</label>
                <select
                  value={uploadPeriodicita}
                  onChange={e => setUploadPeriodicita(e.target.value as Periodicita)}
                  className="bop-select"
                  required
                >
                  <option value="TRIMESTRALE">Trimestrale</option>
                  <option value="MENSILE">Mensile</option>
                </select>
              </div>
            </div>

            <div className="bop-form-group">
              <label className="bop-label">Periodo *</label>
              <select
                value={uploadPeriodo}
                onChange={e => setUploadPeriodo(e.target.value as Periodo)}
                className="bop-select"
                required
              >
                {periodiDisponibili.map(p => <option key={p} value={p}>{labelPeriodo(p)}</option>)}
              </select>
            </div>

            <div className="bop-bilanci__form-row">
              <div className="bop-bilanci__form-row-item">
                <label className="bop-label">IVA a debito</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={uploadIvaDebito}
                  onChange={e => setUploadIvaDebito(e.target.value)}
                  placeholder="0,00"
                  className="bop-input"
                />
              </div>
              <div className="bop-bilanci__form-row-item">
                <label className="bop-label">IVA a credito</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={uploadIvaCredito}
                  onChange={e => setUploadIvaCredito(e.target.value)}
                  placeholder="0,00"
                  className="bop-input"
                />
              </div>
            </div>

            <div className="bop-bilanci__form-row">
              <div className="bop-bilanci__form-row-item">
                <label className="bop-label">Importo versato</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={uploadVersato}
                  onChange={e => setUploadVersato(e.target.value)}
                  placeholder="0,00"
                  className="bop-input"
                />
              </div>
              <div className="bop-bilanci__form-row-item">
                <label className="bop-label">Credito riportato</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={uploadCreditoRiportato}
                  onChange={e => setUploadCreditoRiportato(e.target.value)}
                  placeholder="0,00"
                  className="bop-input"
                />
              </div>
            </div>

            <div className="bop-bilanci__form-row">
              <div className="bop-bilanci__form-row-item">
                <label className="bop-label">Data presentazione</label>
                <input
                  type="date"
                  value={uploadDataPres}
                  onChange={e => setUploadDataPres(e.target.value)}
                  className="bop-input"
                />
              </div>
              <div className="bop-bilanci__form-row-item">
                <label className="bop-label">Protocollo telematico</label>
                <input
                  type="text"
                  value={uploadProtocollo}
                  onChange={e => setUploadProtocollo(e.target.value)}
                  placeholder="Es. 16121234567890-000001"
                  className="bop-input"
                />
              </div>
            </div>

            <div className="bop-form-group">
              <label className="bop-label">Commento</label>
              <textarea
                placeholder="Note sulla comunicazione..."
                value={uploadCommento}
                onChange={e => setUploadCommento(e.target.value)}
                className="bop-bilanci__textarea"
              />
            </div>
          </div>

          <div className="bop-bilanci__form-col">
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`bop-bilanci__dropzone${uploadFiles.length > 0 ? ' bop-bilanci__dropzone--has-file' : ''}`}
            >
              <input
                type="file"
                multiple
                ref={fileInputRef}
                className="bop-bilanci__file-input-hidden"
                onChange={e => {
                  if (e.target.files && e.target.files.length > 0) {
                    setUploadFiles(prev => [...prev, ...Array.from(e.target.files!)]);
                  }
                }}
              />
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={uploadFiles.length > 0 ? '#16a34a' : '#64748b'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="bop-bilanci__dropzone-icon">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="12" y1="18" x2="12" y2="12"></line>
                <line x1="9" y1="15" x2="15" y2="15"></line>
              </svg>
              {uploadFiles.length > 0 ? (
                <div>
                  <div className="bop-bilanci__dropzone-selected-label">{uploadFiles.length} file selezionati:</div>
                  <ul className="bop-lipe__file-list">
                    {uploadFiles.map((f, i) => (
                      <li key={i} className="bop-lipe__file-list-item">
                        <span className="bop-lipe__file-list-name">{f.name}</span>
                        <button
                          type="button"
                          className="bop-lipe__file-list-remove"
                          onClick={(ev) => { ev.stopPropagation(); removeFileFromUpload(i); }}
                          aria-label="Rimuovi"
                        >×</button>
                      </li>
                    ))}
                  </ul>
                  <div className="bop-bilanci__dropzone-selected-hint">Clicca o trascina per aggiungere altri</div>
                </div>
              ) : (
                <div className="bop-bilanci__dropzone-placeholder">
                  <span className="bop-bilanci__dropzone-link">Clicca per esplorare</span> oppure trascina e rilascia<br />
                  <span className="bop-bilanci__dropzone-hint">Puoi caricare più file insieme</span>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={isUploading}
              className="bop-bilanci__upload-btn"
            >
              {isUploading ? (
                <>
                  <span className="bop-bilanci__upload-spinner"></span>
                  Caricamento in corso...
                </>
              ) : (
                'Carica LIPE'
              )}
            </button>
          </div>
        </form>
      </div>

      {/* ── LISTA LIPE ── */}
      <div className="bop-bilanci__archive-card">
        <div className="bop-bilanci__archive-header">
          <h2 className="bop-bilanci__archive-title">Archivio LIPE</h2>

          <div className="bop-bilanci__archive-filters">
            {!isUnica && (
              <select
                value={filterSocietaId}
                onChange={e => setFilterSocietaId(e.target.value)}
                className="bop-bilanci__filter-select"
              >
                <option value="">Tutte le società...</option>
                {societaList.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
            )}
            <select
              value={filterAnno}
              onChange={e => setFilterAnno(e.target.value)}
              className="bop-bilanci__filter-select"
            >
              <option value="">Tutti gli anni...</option>
              {selectYears.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select
              value={filterPeriodicita}
              onChange={e => setFilterPeriodicita(e.target.value)}
              className="bop-bilanci__filter-select"
            >
              <option value="">Mensile + Trimestrale</option>
              <option value="TRIMESTRALE">Solo trimestrale</option>
              <option value="MENSILE">Solo mensile</option>
            </select>
          </div>
        </div>

        {isLoading ? (
          <div className="bop-bilanci__loading">Caricamento archivio...</div>
        ) : lipe.length === 0 ? (
          <div className="bop-bilanci__empty">
            Nessuna LIPE trovata per i filtri selezionati.
          </div>
        ) : (
          <div className="bop-table-wrapper bop-lipe__table-wrapper">
            <table className="bop-table bop-lipe__table">
              <thead>
                <tr>
                  <th>Società</th>
                  <th className="bop-text-right">Anno</th>
                  <th>Periodo</th>
                  <th className="bop-text-right">IVA debito</th>
                  <th className="bop-text-right">IVA credito</th>
                  <th className="bop-text-right">Versato</th>
                  <th>Data pres.</th>
                  <th>Protocollo</th>
                  <th>Caricato da</th>
                  <th className="bop-text-center">Allegati</th>
                  <th className="bop-table__th--actions">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {lipe.map(l => (
                  <tr key={l.id}>
                    <td className="bop-lipe__td-societa">{l.societa.nome}</td>
                    <td className="bop-text-right bop-lipe__td-num">{l.anno}</td>
                    <td>
                      <span className={`bop-badge ${l.periodicita === 'TRIMESTRALE' ? 'bop-badge--info' : 'bop-badge--neutral'}`}>
                        {labelPeriodo(l.periodo)}
                      </span>
                    </td>
                    <td className="bop-text-right bop-lipe__td-num">{l.ivaDebito !== null ? formatCurrency(l.ivaDebito) : '—'}</td>
                    <td className="bop-text-right bop-lipe__td-num">{l.ivaCredito !== null ? formatCurrency(l.ivaCredito) : '—'}</td>
                    <td className="bop-text-right bop-lipe__td-num">{l.importoVersato !== null ? formatCurrency(l.importoVersato) : '—'}</td>
                    <td className="bop-lipe__td-meta">
                      {l.dataPresentazione ? new Date(l.dataPresentazione).toLocaleDateString('it-IT') : '—'}
                    </td>
                    <td className="bop-lipe__td-meta">
                      {l.protocolloTelem ? (
                        <span title={l.protocolloTelem} className="bop-lipe__td-protocollo">
                          {l.protocolloTelem}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="bop-lipe__td-meta">{l.uploadedBy.nome}</td>
                    <td className="bop-text-center">
                      <button
                        type="button"
                        className={`bop-lipe__allegati-chip${l.allegati.length === 0 ? ' bop-lipe__allegati-chip--empty' : ''}`}
                        onClick={() => setAllegatiModal(l)}
                        title={l.allegati.length === 0 ? 'Nessun allegato — clicca per aggiungerne' : `${l.allegati.length} allegati — clicca per gestire`}
                      >
                        <PaperclipIcon />
                        <span>{l.allegati.length}</span>
                      </button>
                    </td>
                    <td className="bop-table__td--actions">
                      <BopRowActions actions={[
                        {
                          icon: <PaperclipIcon />,
                          label: 'Gestisci allegati',
                          variant: 'primary',
                          onClick: () => setAllegatiModal(l),
                        },
                        {
                          icon: <RowIcon.Trash />,
                          label: 'Elimina LIPE',
                          variant: 'danger',
                          hidden: userRole !== 'SUPERADMIN',
                          onClick: () => handleDelete(l.id),
                        },
                      ]} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODALE GESTIONE ALLEGATI ── */}
      {allegatiModal && (
        <div className="bop-modal-overlay" onClick={() => setAllegatiModal(null)}>
          <div className="bop-modal bop-lipe__modal" onClick={e => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">
                Allegati LIPE — {allegatiModal.societa.nome}, {allegatiModal.anno} {labelPeriodo(allegatiModal.periodo)}
              </h3>
              <button className="bop-modal__close" onClick={() => setAllegatiModal(null)} aria-label="Chiudi">×</button>
            </div>
            <div className="bop-modal__body">
              {allegatiModal.allegati.length === 0 ? (
                <div className="bop-lipe__modal-empty">Nessun allegato presente.</div>
              ) : (
                <ul className="bop-lipe__modal-list">
                  {allegatiModal.allegati.map(a => (
                    <li key={a.id} className="bop-lipe__modal-item">
                      <div className="bop-lipe__modal-item-info">
                        <div className="bop-lipe__modal-item-name">{a.nomeFile}</div>
                        <div className="bop-lipe__modal-item-meta">
                          Caricato il {new Date(a.uploadedAt).toLocaleDateString('it-IT')} da {a.uploadedBy.nome}
                        </div>
                      </div>
                      <div className="bop-lipe__modal-item-actions">
                        <button
                          className="bop-btn-secondary"
                          onClick={() => handleDownloadAllegato(allegatiModal.id, a.id, a.nomeFile)}
                        >
                          Scarica
                        </button>
                        <button
                          className="bop-btn-danger"
                          onClick={() => handleDeleteAllegato(allegatiModal.id, a.id, a.nomeFile)}
                        >
                          Elimina
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <div className="bop-lipe__modal-add">
                <input
                  type="file"
                  multiple
                  ref={modalFileInputRef}
                  className="bop-bilanci__file-input-hidden"
                  onChange={handleAddAllegati}
                />
                <button
                  type="button"
                  className="bop-btn-primary"
                  disabled={allegatiUploading}
                  onClick={() => modalFileInputRef.current?.click()}
                >
                  {allegatiUploading ? 'Caricamento...' : '+ Aggiungi allegati'}
                </button>
              </div>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn-secondary" onClick={() => setAllegatiModal(null)}>Chiudi</button>
            </div>
          </div>
        </div>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
